import type { Filter } from '#layers/data-model/types/filters';
import type { PrimaryKey } from '#layers/data-model/types/schema/primary-key';
import type { ResolvedMonospacePrimitiveField } from '#layers/data-model/types/schema/resolved-schema';
import { isNil, mapValues } from '#layers/base/public';
import { isLogicalFilterAnd, isLogicalFilterOr } from '#layers/data-model/utils/filters/index';
import { pickByPrimitiveFields } from '#layers/data-model/utils/item';
import { isCompositePrimaryKey } from '#layers/data-model/utils/primary-key';

export function mergeAnd(...filters: Filter[]) {
	const result: Filter[] = [];

	for (const filter of filters) {
		if (isLogicalFilterAnd(filter)) {
			result.push(...filter._and);
		}
		else {
			result.push(filter);
		}
	}

	return { _and: result };
}

export function mergeOr(...filters: Filter[]) {
	const result: Filter[] = [];

	for (const filter of filters) {
		if (isLogicalFilterOr(filter)) {
			result.push(...filter._or);
		}
		else {
			result.push(filter);
		}
	}

	return { _or: result };
}

export interface BuildFilterFromItemKeysOptions {
	keys: PrimaryKey[];
	primaryKeyFields: ResolvedMonospacePrimitiveField[];
}

/**
 * Builds a filter for querying items by their primary keys
 *
 * uses `_in` operator for single primary keys
 * and `_or` conditions for composite keys
 *
 * @param options - Filter construction options
 * @param options.keys - Array of primary key values (strings or objects)
 * @param options.primaryKeyFields - Field definitions from the Monospace collection schema
 *
 * @returns filter object ready for API queries
 */
export function buildFilterFromItemKeys({ keys, primaryKeyFields }: BuildFilterFromItemKeysOptions): Filter {
	if (keys.length === 0) return {};

	if (primaryKeyFields.length === 1) {
		const fieldName = primaryKeyFields[0]!.apiName;
		const values = keys
			.map((key) => isCompositePrimaryKey(key) ? key[fieldName] : key)
			.filter((value) => !isNil(value));

		return {
			[fieldName]: { _in: values },
		};
	}

	const keyFilters = keys
		.filter((key) => isCompositePrimaryKey(key))
		.map((key) =>
			mapValues(pickByPrimitiveFields(key, primaryKeyFields), (value) => ({ _eq: value })),
		);

	return { _or: keyFilters };
}
